import { Component, signal, ChangeDetectionStrategy, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalLayoutComponent } from '@program-suite/ui';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UI_FORM_IMPORTS } from '@program-suite/ui';
import { ButtonComponent } from '@program-suite/ui';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LookupViewModel, UserViewModel } from '@program-suite/data-common';
import { LookupStore } from '@program-suite/data-common';
import { AdminSettingsStore } from '../../data/admin-settings.store';
import { SnackbarComponent, ToastType } from '@program-suite/ui';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface addAdminData {
  title: string;
  subTitle: string;
  pTitle: string;
  pClosing?: string;
  button: string;
  user?: UserViewModel;
}

@Component({
  selector: 'lib-add-admin',
  templateUrl: './add-admin.component.html',
  styleUrls: ['./add-admin.component.scss', '../../admin-settings/admin-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalLayoutComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    ...UI_FORM_IMPORTS
],
})
export class AddAdminComponent {
  data: addAdminData = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<AddAdminComponent>);
  lookupStore = inject(LookupStore);
  adminSettingsStore = inject(AdminSettingsStore);
  userForm!: FormGroup;
  display: boolean = false;
  statusOptions = signal<LookupViewModel[]>([
    { id: 'Active', name: 'Active' },
    { id: 'InActive', name: 'Inactive' },
  ]);
  roles = computed<LookupViewModel[]>(() => {
    const roles = this.lookupStore.wholesalerRoles();
    return roles
      ? roles.map((role) => {
          return { id: role.name, name: role.name };
        })
      : [];
  });
  constructor(private fb: FormBuilder, private snackBar: MatSnackBar) {
    this.lookupStore.loadLookup('wholesalerRoles');

    // Todo: We may need a shared UserInfoForm component to handle this
    this.userForm = this.fb.group({
      firstName: [this.data?.user?.firstName ?? '', Validators.required],
      lastName: [this.data?.user?.lastName ?? '', Validators.required],
      email: [this.data?.user?.email ?? '', [Validators.required, Validators.email]],
      role: [this.data?.user?.roles ? this.data.user?.roles[0] : '', Validators.required],
      status: [this.data?.user?.status ?? '', Validators.required],
    });
  }

  submit() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }
    const user = this.userForm.value;

    if (this.data?.user) {
      const form: UserViewModel = {
        id: this.data.user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        roles: [user.role],
        status: user.status,
      };
      this.adminSettingsStore
        .updateUser(form)
        .then(() => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 3000,
            data: { toastType: ToastType.Success, message: 'Updated User' },
          });
          this.dialogRef.close();
        })
        .catch((error) => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 3000,
            data: { toastType: ToastType.Error, message: error.message },
          });
        });
    } else {
      const form: UserViewModel = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        roles: [user.role],
        status: user.status,
      };
      this.adminSettingsStore
        .inviteUser(form)
        .then(() => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 3000,
            data: { toastType: ToastType.Success, message: 'Admin user invitation sent.' },
          });
          this.dialogRef.close();
        })
        .catch((error) => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 3000,
            data: { toastType: ToastType.Error, message: error.message },
          });
        });
    }
  }
}
